import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    CarApproveFormErrors,
    CarApproveFormErrorsSchema,
    CarCreateFormErrors,
    CarCreateFormErrorsSchema,
    CarsPaginator,
    CarsPaginatorSchema,
    CarDecodedComplectation,
    CarDecodedComplectationSchema,
    Car,
    CarSchema,
    CarSendToModerationErrorsSchema,
    CarSendToModerationErrors,
    CarUpdateFormErrors,
    CarUpdateFormErrorsSchema,
    CarRejectFormErrorsSchema,
    CarRejectFormErrors,
} from '@/types/car.type.ts';
import { convertFlatMapToNestedMap } from '@/utils/collection-helpers.ts';

class CarRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getCars(oQuery: any): Promise<CarsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: oQuery,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetCars', requestParams)
                .then(({ data }: any) => {
                    const parsedCarsPaginator = CarsPaginatorSchema.parse(data);
                    const carsPaginator: CarsPaginator = parsedCarsPaginator as unknown as CarsPaginator;

                    resolve(carsPaginator);
                });
        });
    }

    public approveCar(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('ApproveCar', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = CarApproveFormErrorsSchema.parse(errors);
                        const approveErrors = parsedErrors as unknown as CarApproveFormErrors;

                        reject(approveErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public rejectCar(id: number, comment: string): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: { comment },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('RejectCar', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = CarRejectFormErrorsSchema.parse(errors);
                        const rejectErrors: CarRejectFormErrors = parsedErrors as unknown as CarRejectFormErrors;

                        reject(rejectErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public createCar(oQuery: any): Promise<number>
    {
        const requestParams: ApiRequestParams = {
            requestBody: oQuery,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('CreateCar', requestParams)
                .then(({ data: { car_id } }: any) => {
                    resolve(car_id);
                })
                .catch(({ response: { data: { errors } } }: any) => {
                    const nestedErrors = convertFlatMapToNestedMap(errors);
                    const parsedErrors = CarCreateFormErrorsSchema.parse(nestedErrors);
                    const createErrors: CarCreateFormErrors = parsedErrors as unknown as CarCreateFormErrors;

                    reject(createErrors);
                });
        });
    }

    public decodeVin(vin: string): Promise<CarDecodedComplectation>
    {
        const requestParams: ApiRequestParams = {
            segments: { vin },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('DecodeVin', requestParams)
                .then(({ data: { complectation } }: any) => {
                    const parsedComplectation = CarDecodedComplectationSchema.parse(complectation);
                    const decodedComplectation: CarDecodedComplectation = parsedComplectation as unknown as CarDecodedComplectation;

                    resolve(decodedComplectation);
                })
                .catch(({ response: { status } }: any) => {
                    if (status === 404) {
                        reject();
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public getCar(id: number): Promise<Car>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetCar', requestParams)
                .then(({ data }: any) => {
                    const parsedCar = CarSchema.parse(data);
                    const car = parsedCar as unknown as Car;

                    resolve(car);
                });
        });
    }

    public updateCar(id: number, oQuery: any): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: oQuery,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('UpdateCar', requestParams)
                .then(() => {
                    resolve();
                })
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedErrors = CarUpdateFormErrorsSchema.parse(nestedErrors);
                        const updateErrors: CarUpdateFormErrors = parsedErrors as unknown as CarUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public sendToModerationCar(id: number, oQuery: any): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: oQuery,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('SendCarToModeration', requestParams)
                .then(() => {
                    resolve();
                })
                .catch(({ response: { data: { errors } } }: any) => {
                    const parsedErrors = CarSendToModerationErrorsSchema.parse(errors);
                    const moderationErrors: CarSendToModerationErrors = parsedErrors as unknown as CarSendToModerationErrors;

                    reject(moderationErrors);
                });
        });
    }

    public deleteCar(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('DeleteCar', requestParams)
                .then(() => resolve());
        });
    }

    public recreateCar(id: number): Promise<number>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('RecreateCar', requestParams)
                .then(({ data: { car_id } }: any) => {
                    resolve(car_id);
                })
                .catch(() => {
                    reject();
                });
        });
    }
}

export default CarRepository;
